import { createContext, useContext, useState, useEffect, ReactNode, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { useQueryClient } from "@tanstack/react-query";

interface WorkspaceMember {
  id: string;
  owner_id: string;
  member_id: string;
  role: string;
  joined_at: string | null;
  owner_username?: string;
  owner_display_name?: string;
}

interface WorkspaceContextType {
  activeOwnerId: string | null;
  isOwnWorkspace: boolean;
  memberRole: string | null;
  isViewer: boolean;
  isEditor: boolean;
  workspaces: WorkspaceMember[];
  switchWorkspace: (ownerId: string | null) => void;
  isLoading: boolean;
}

const WorkspaceContext = createContext<WorkspaceContextType>({
  activeOwnerId: null,
  isOwnWorkspace: true,
  memberRole: null,
  isViewer: false,
  isEditor: false,
  workspaces: [],
  switchWorkspace: () => {},
  isLoading: false,
});

export const useWorkspace = () => useContext(WorkspaceContext);

export function WorkspaceProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [activeOwnerId, setActiveOwnerId] = useState<string | null>(null);
  const [workspaces, setWorkspaces] = useState<WorkspaceMember[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // Set default activeOwnerId when user loads
  useEffect(() => {
    if (user?.id && !activeOwnerId) {
      setActiveOwnerId(user.id);
    }
    if (!user) {
      setActiveOwnerId(null);
      setWorkspaces([]);
    }
  }, [user?.id]);

  // Fetch workspaces where user is a member
  useEffect(() => {
    if (!user) return;

    const fetchWorkspaces = async () => {
      setIsLoading(true);
      try {
        const { data: members, error } = await (supabase
          .from("workspace_members")
          .select("*")
          .eq("member_id", user.id) as any);

        if (error) throw error;
        if (!members?.length) {
          setWorkspaces([]);
          setIsLoading(false);
          return;
        }

        // Fetch owner profiles
        const ownerIds = members.map((m: any) => m.owner_id);
        const { data: profiles } = await (supabase as any)
          .from("workspace_profiles")
          .select("id, username, display_name")
          .in("id", ownerIds);

        const profileMap = new Map<string, { id: string; username: string; display_name: string | null }>(
          (profiles ?? []).map((p: any) => [p.id, p])
        );

        const enriched: WorkspaceMember[] = members.map((m: any) => ({
          ...m,
          owner_username: profileMap.get(m.owner_id)?.username ?? "Unknown",
          owner_display_name: profileMap.get(m.owner_id)?.display_name ?? null,
        }));

        setWorkspaces(enriched);
      } catch (err) {
        console.error("Failed to fetch workspaces:", err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchWorkspaces();
  }, [user?.id]);

  const isOwnWorkspace = activeOwnerId === user?.id;

  const currentMembership = workspaces.find(
    (w) => w.owner_id === activeOwnerId
  );
  const memberRole = isOwnWorkspace ? null : (currentMembership?.role ?? null);
  const isViewer = !isOwnWorkspace && memberRole === "viewer";
  const isEditor = !isOwnWorkspace && memberRole === "editor";

  const switchWorkspace = useCallback(
    (ownerId: string | null) => {
      const newOwner = ownerId ?? user?.id ?? null;
      setActiveOwnerId(newOwner);
      // Invalidate all data queries so they refetch with new owner
      queryClient.invalidateQueries();
    },
    [user?.id, queryClient]
  );

  return (
    <WorkspaceContext.Provider
      value={{
        activeOwnerId,
        isOwnWorkspace,
        memberRole,
        isViewer,
        isEditor,
        workspaces,
        switchWorkspace,
        isLoading,
      }}
    >
      {children}
    </WorkspaceContext.Provider>
  );
}
